import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Divider,
  Box,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import TableViewIcon from "@mui/icons-material/TableView";

import toast from "react-hot-toast";

const BulkUploadDialog = ({
  open,
  onClose,
  onUpload,
  loading = false,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | Download CSV Template
  |--------------------------------------------------------------------------
  */

  const handleDownloadCsvTemplate = () => {
    const link = document.createElement("a");

    link.href = "/assets/employees_bulk_upload_clean.csv";
    link.download = "employees_bulk_upload_clean.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /*
  |--------------------------------------------------------------------------
  | Download Excel Template
  |--------------------------------------------------------------------------
  */

  const handleDownloadExcelTemplate = () => {
    const link = document.createElement("a");

    link.href = "/assets/employees_bulk_upload_clean.xlsx";
    link.download = "employees_bulk_upload_clean.xlsx";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /*
  |--------------------------------------------------------------------------
  | File Selection
  |--------------------------------------------------------------------------
  */

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const extension = file.name
      .split(".")
      .pop()
      .toLowerCase();

    const allowedExtensions = [
      "csv",
      "xls",
      "xlsx",
    ];

    if (!allowedExtensions.includes(extension)) {
      toast.error("Please select a CSV or Excel file.");
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  /*
  |--------------------------------------------------------------------------
  | Upload
  |--------------------------------------------------------------------------
  */

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file.");
      return;
    }

    await onUpload(selectedFile);
  };

  /*
  |--------------------------------------------------------------------------
  | Close
  |--------------------------------------------------------------------------
  */

  const handleClose = () => {
    if (loading) return;

    setSelectedFile(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="h6"
            fontWeight={600}
          >
            Bulk Upload
          </Typography>

          <IconButton
            onClick={handleClose}
            size="small"
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={3}>

          {/* Download Template */}

          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              gutterBottom
            >
              Download Demo Template
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Download the template and use the same format
              for bulk employee upload.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
            >
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={handleDownloadCsvTemplate}
                fullWidth
              >
                CSV Template
              </Button>

              <Button
                variant="outlined"
                startIcon={<TableViewIcon />}
                onClick={handleDownloadExcelTemplate}
                fullWidth
              >
                Excel Template
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* Upload */}

          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              gutterBottom
            >
              Upload File
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Select a CSV or Excel file to upload.
            </Typography>

            <input
              type="file"
              id="bulk-upload-file"
              hidden
              accept=".csv,.xls,.xlsx"
              onChange={handleFileChange}
            />

            <label htmlFor="bulk-upload-file">
              <Button
                component="span"
                variant="contained"
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                Choose CSV / Excel File
              </Button>
            </label>

            {selectedFile && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "grey.100",
                }}
              >
                <Typography variant="body2">
                  <strong>Selected File:</strong>{" "}
                  {selectedFile.name}
                </Typography>
              </Box>
            )}
          </Box>

        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button
          color="error"
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          disabled={!selectedFile || loading}
          onClick={handleUpload}
        >
          {loading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadDialog;